import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, CreditCard, Building2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

interface PaymentMethodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: {
    id: string;
    invoice_number: string;
    total_amount: number;
    currency: string;
  };
}

const BANK_DETAILS = {
  bank: "CIH Bank",
  accountName: "Ismail Ibba",
  rib: "230727416910521101660011"
};

export const PaymentMethodDialog = ({ open, onOpenChange, invoice }: PaymentMethodDialogProps) => {
  const [paymentMethod, setPaymentMethod] = useState<"bank" | "card">("bank");
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const queryClient = useQueryClient();

  const handleBankTransferProof = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${invoice.invoice_number}_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('payment-proofs')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('payment-proofs')
        .getPublicUrl(fileName);

      // Update invoice with payment proof
      const { error: updateError } = await supabase
        .from('invoices')
        .update({ 
          payment_proof_url: publicUrl,
          payment_method: 'bank_transfer',
          status: 'pending' // Changed to pending for review
        })
        .eq('id', invoice.id);

      if (updateError) throw updateError;

      toast.success("Payment proof uploaded successfully! We'll review it shortly.");
      queryClient.invalidateQueries({ queryKey: ["client-invoices"] });
      onOpenChange(false);
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || "Failed to upload payment proof");
    } finally {
      setUploading(false);
    }
  };

  const handleCardPayment = async () => {
    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke('youcanpay-payment', {
        body: {
          invoiceId: invoice.id,
          amount: invoice.total_amount,
          currency: invoice.currency || 'MAD',
        }
      });

      if (error) throw error;

      // Update invoice with payment method
      await supabase
        .from('invoices')
        .update({ payment_method: 'credit_card' })
        .eq('id', invoice.id);

      // Redirect to YouCanPay checkout
      if (data.checkout_url) {
        window.open(data.checkout_url, '_blank');
        toast.success("Redirecting to payment page...");
        onOpenChange(false);
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error(error.message || "Failed to initiate payment");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Pay Invoice #{invoice.invoice_number}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Amount Due</span>
              <span className="text-2xl font-bold">{invoice.currency} {invoice.total_amount.toFixed(2)}</span>
            </div>
          </div>

          <RadioGroup value={paymentMethod} onValueChange={(value: any) => setPaymentMethod(value)}>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 rounded-lg border border-border p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="bank" id="bank" />
                <Label htmlFor="bank" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Building2 className="h-5 w-5" />
                  <span className="font-medium">Bank Transfer</span>
                </Label>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border border-border p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="card" id="card" />
                <Label htmlFor="card" className="flex items-center gap-2 cursor-pointer flex-1">
                  <CreditCard className="h-5 w-5" />
                  <span className="font-medium">Credit/Debit Card (YouCanPay)</span>
                </Label>
              </div>
            </div>
          </RadioGroup>

          {paymentMethod === "bank" && (
            <div className="space-y-4 rounded-lg border border-border bg-card p-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Bank Transfer Details
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bank:</span>
                  <span className="font-medium">{BANK_DETAILS.bank}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Name:</span>
                  <span className="font-medium">{BANK_DETAILS.accountName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">RIB:</span>
                  <span className="font-mono font-medium">{BANK_DETAILS.rib}</span>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Label htmlFor="proof" className="text-sm font-medium mb-2 block">
                  Upload Payment Proof
                </Label>
                <div className="relative">
                  <Input
                    id="proof"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleBankTransferProof}
                    disabled={uploading}
                    className="cursor-pointer"
                  />
                  {uploading && (
                    <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-md">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-r-transparent" />
                        Uploading...
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Upload a screenshot or photo of your bank transfer receipt
                </p>
              </div>
            </div>
          )}

          {paymentMethod === "card" && (
            <Button 
              onClick={handleCardPayment} 
              disabled={processing}
              className="w-full"
              size="lg"
            >
              {processing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-r-transparent" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay with Card
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
